import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLanguage } from "@/hooks/useLanguage";
import { logger } from "@/lib/logger";

// Character trait type definition
type TraitType = "personality_traits" | "strengths" | "weaknesses" | "skills";

// Character data structure type
interface CharacterData {
  id: string;
  name: string;
  is_public: boolean;
  personality_traits: string[];
  strengths: string[];
  weaknesses: string[];
  skills: string[];
  [key: string]: any; // Allow dynamic access to properties
}

export const CharacterStatsTab = () => {
  const { t } = useLanguage();
  const [primaryAxis, setPrimaryAxis] = useState<TraitType>("personality_traits");
  const [secondaryAxis, setSecondaryAxis] = useState<TraitType>("strengths");

  // Original query for basic user stats
  const { data: stats, isLoading: isLoadingStats } = useQuery({
    queryKey: ['userStats'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('user_stats')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      return data;
    }
  });

  // Separate query for character data needed for trait visualization
  const { data: characters, isLoading: isLoadingCharacters } = useQuery({
    queryKey: ['userCharacterTraits', primaryAxis, secondaryAxis],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Fetch characters with traits
      const { data, error } = await supabase
        .from('characters')
        .select(`
          id, name, is_public,
          personality_traits, strengths, weaknesses, skills
        `)
        .eq('user_id', user.id);

      if (error) throw error;
      return data as CharacterData[];
    }
  });

  // Show loading state if either query is loading
  if (isLoadingStats || isLoadingCharacters) {
    return <div className="text-white/60">{t('dashboard.stats.characterStats.loading')}</div>;
  }

  // Helper function to get all unique items from array fields
  const getUniqueTraits = (traitType: TraitType): string[] => {
    // Combine traits from all characters
    const allTraits = (characters || []).flatMap(character => {
      // Safely access arrays or default to empty arrays
      return Array.isArray(character[traitType]) ? character[traitType] : [];
    });
    
    // Remove duplicates and empty values
    return [...new Set(allTraits)].filter(Boolean);
  };

  // Process data for heat map
  const processHeatMapData = () => {
    // Get all unique traits for both axes
    const primaryTraits = getUniqueTraits(primaryAxis);
    const secondaryTraits = getUniqueTraits(secondaryAxis);
    
    // Create frequency map
    const frequencyMap: Record<string, Record<string, number>> = {};
    
    (characters || []).forEach(character => {
      // Get primary traits
      const primaryItems = Array.isArray(character[primaryAxis]) ? character[primaryAxis] : [];
      
      // Get secondary traits
      const secondaryItems = Array.isArray(character[secondaryAxis]) ? character[secondaryAxis] : [];
      
      // Increment count for each combination
      primaryItems.forEach(primary => {
        if (primary) {
          if (!frequencyMap[primary]) {
            frequencyMap[primary] = {};
          }
          
          secondaryItems.forEach(secondary => {
            if (secondary) {
              if (!frequencyMap[primary][secondary]) {
                frequencyMap[primary][secondary] = 0;
              }
              frequencyMap[primary][secondary]++;
            }
          });
        }
      });
    });

    // Convert to array format for the chart
    const heatmapData = Object.entries(frequencyMap).map(([primary, secondaries]) => {
      // Create base object with name
      const dataPoint: Record<string, any> = { name: primary };
      
      // Add count for each secondary trait
      secondaryTraits.forEach(secondary => {
        dataPoint[secondary] = secondaries[secondary] || 0;
      });
      
      // Calculate total for sorting
      const secondaryValues = Object.values(secondaries) as number[];
      dataPoint.total = secondaryValues.length > 0 ? 
        secondaryValues.reduce((sum, count) => sum + count, 0) : 0;
      
      return dataPoint;
    });
    
    // Sort by total frequency for better visualization
    heatmapData.sort((a, b) => b.total - a.total);
    
    // Return data limited to top items for better display
    return {
      data: heatmapData.slice(0, 12), // Limit to top 12 for readability
      secondaryTraits: secondaryTraits.slice(0, 12) // Limit to top 12
    };
  };

  // Use try-catch to handle any potential errors in data processing
  let heatmapData = [];
  let secondaryTraits: string[] = [];
  
  try {
    const processedData = processHeatMapData();
    heatmapData = processedData.data;
    secondaryTraits = processedData.secondaryTraits;
  } catch (error) {
    logger.error("Error processing heatmap data:", error);
  }
  
  // Generate colors based on value
  const getColorIntensity = (value: number) => {
    if (value === 0) return 'rgba(105, 0, 109, 0.1)';
    const maxValue = 10; // Adjust based on your data
    const intensity = Math.min(value / maxValue, 1);
    return `rgba(0, 0, 0, ${0.3 + intensity * 0.7})`;
  };

  // Get translated trait type names
  const getTraitTypeName = (traitType: TraitType): string => {
    switch (traitType) {
      case "personality_traits":
        return t('dashboard.stats.characterStats.personalityTraits');
      case "strengths":
        return t('dashboard.stats.characterStats.strengths');
      case "weaknesses":
        return t('dashboard.stats.characterStats.weaknesses');
      case "skills":
        return t('dashboard.stats.characterStats.skills');
      default:
        return traitType;
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4 bg-white/5">
          <h3 className="text-sm font-medium text-gray-400">{t('dashboard.stats.characterStats.totalCharacters')}</h3>
          <p className="text-2xl font-bold text-white">{stats?.total_characters || 0}</p>
        </Card>
        <Card className="p-4 bg-white/5">
          <h3 className="text-sm font-medium text-gray-400">{t('dashboard.stats.characterStats.publicCharacters')}</h3>
          <p className="text-2xl font-bold text-white">{stats?.public_characters || 0}</p>
        </Card>
        <Card className="p-4 bg-white/5">
          <h3 className="text-sm font-medium text-gray-400">{t('dashboard.stats.characterStats.averageRating')}</h3>
          <p className="text-2xl font-bold text-white">{stats?.avg_character_rating?.toFixed(1) || '0.0'}</p>
        </Card>
      </div>

      <Card className="p-4 sm:p-6 bg-white/5">
        <h3 className="text-lg font-medium text-white mb-4">{t('dashboard.stats.characterStats.traitRelationships')}</h3>
        <div className="flex gap-4 mb-6">
          <div className="flex-1">
            <label className="block text-sm text-gray-400 mb-1">{t('dashboard.stats.characterStats.primaryTraitType')}</label>
            <Select 
              value={primaryAxis} 
              onValueChange={(value) => setPrimaryAxis(value as TraitType)}
            >
              <SelectTrigger className="bg-white/10 border-white/20 text-white">
                <SelectValue placeholder={t('dashboard.stats.characterStats.selectTraitType')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="personality_traits">{getTraitTypeName("personality_traits")}</SelectItem>
                <SelectItem value="strengths">{getTraitTypeName("strengths")}</SelectItem>
                <SelectItem value="weaknesses">{getTraitTypeName("weaknesses")}</SelectItem>
                <SelectItem value="skills">{getTraitTypeName("skills")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex-1">
            <label className="block text-sm text-gray-400 mb-1">{t('dashboard.stats.characterStats.secondaryTraitType')}</label>
            <Select 
              value={secondaryAxis} 
              onValueChange={(value) => setSecondaryAxis(value as TraitType)}
            >
              <SelectTrigger className="bg-white/10 border-white/20 text-white">
                <SelectValue placeholder={t('dashboard.stats.characterStats.selectTraitType')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="personality_traits">{getTraitTypeName("personality_traits")}</SelectItem>
                <SelectItem value="strengths">{getTraitTypeName("strengths")}</SelectItem>
                <SelectItem value="weaknesses">{getTraitTypeName("weaknesses")}</SelectItem>
                <SelectItem value="skills">{getTraitTypeName("skills")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="h-[400px] chart-container">
          {heatmapData.length === 0 ? (
            <div className="flex items-center justify-center h-full text-gray-400">
              {t('dashboard.stats.storyStats.noDataAvailable')}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={heatmapData}
                layout="vertical"
                margin={{
                  top: 10,
                  right: window.innerWidth < 768 ? 0 : 30,
                  left: window.innerWidth < 768 ? -30 : 100,
                  bottom: 30,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis type="number" stroke="#999" />
                <YAxis 
                  dataKey="name" 
                  type="category" 
                  stroke="#999" 
                  width={100} // Adjusted width for long labels
                  tick={{ fill: '#999', fontSize: 12 }} 
                />
                <Tooltip
                  formatter={(value, name) => [`${value} ${t('dashboard.stats.characterStats.characters')}`, name]}
                  contentStyle={{ 
                    backgroundColor: '#1a1f2c',
                    border: '1px solid #333',
                    borderRadius: '4px',
                    color: '#fff'                    
                  }}
                  labelStyle={{ color: '#fff', fontWeight: 'bold' }}  
                  itemStyle={{ color: '#fff' }}  
                />
                {secondaryTraits.map((trait) => (
                  <Bar 
                    key={trait} 
                    dataKey={trait} 
                    stackId="a" 
                    name={trait} 
                    radius={[0, 4, 4, 0]}
                  >
                    {heatmapData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={getColorIntensity(entry[trait] || 0)}
                      />
                    ))}
                  </Bar>
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        
        <div className="mt-4 flex justify-center">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">{t('dashboard.stats.characterStats.lessFrequent')}</span>
            <div className="flex h-4">
              {[0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1].map((opacity, i) => (
                <div 
                  key={i}
                  className="w-5 h-full" 
                  style={{ backgroundColor: `rgba(0, 0, 0, ${opacity})` }}
                />
              ))}
            </div>
            <span className="text-xs text-gray-400">{t('dashboard.stats.characterStats.moreFrequent')}</span>
          </div>
        </div>
        
        <p className="text-xs text-gray-400 text-center mt-2">
          {t('dashboard.stats.characterStats.usageDescription')}
        </p>
      </Card>
    </div>
  );
};
